import { A } from '@adornis/base/env-info.js';
import type { RegistrationUserDataPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-link.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import '@adornis/internationalization/client/language-picker/x-country-picker.js';
import '@adornis/internationalization/client/x-tel-input.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { type IcsrmStammdaten } from '../stammdaten/icsrm-stammdaten-plugin.js';

export const renderIcsrmFields = (
  stContr: GenericFieldFormController<IcsrmStammdaten>,
  translationController: TranslationController<BHTranslationDictionary>,
) => {
  const genericFieldOption = (fieldName: string) =>
    ({
      placeholder: translationController.translate('icsrm', 'field', fieldName as any) as string,
      placeholderMode: 'static-floating',
    } as const);

  const countryPicker = (key: Parameters<typeof stContr.field>[0]) => html`
    <x-country-picker
      showflag
      language=${globalLanguageSubject.getValue()}
      placeholder=${translationController.translate('icsrm', 'field', key as any) + '*'}
      ${stContr.field(key)}
    >
    </x-country-picker>
  `;

  return html`
      <x-flex space="lg">
        <!-- personal data -->
        <x-flex space="sm">
          ${stContr.genericField(['gender'], genericFieldOption('gender'))}

          <!-- nationality -->
          ${countryPicker('nationality')}

            <!-- country of birth -->
          ${countryPicker('countryOfBirth')}

          <!-- phone number -->
          <x-tel-input
            placeholder=${translationController.translate('icsrm', 'field', 'phoneNumber') + '*'}
            ${stContr.field('phoneNumber')}
          ></x-tel-input>

          <!-- addresse -->
          <x-flex space="md">
            <tr-h4> Adresse </tr-h4>
            <x-flex space="sm">
              <x-flex horizontal space="sm">
                ${stContr.genericField(['address', 'zipCity', 'zip'], genericFieldOption('zip'))}
                <div style="flex: 1">${stContr.genericField(['address', 'zipCity', 'city'], genericFieldOption('city'))}</div>
              </x-flex>
            </x-flex>

            <tr-dropdown-selection
              ${stContr.field('isImmigrant')}
              placeholder=${translationController.translate('icsrm', 'isImmigrantExpl') + '*'}
              .selectables=${[true, false]}
              .renderString=${key => translationController.translate(key ? 'yes' : 'no')}
            ></tr-dropdown-selection>

            <tr-dropdown-selection
              ${stContr.field('foreignUniQualification')}
              placeholder=${translationController.translate('icsrm', 'field', 'foreignUniQualification') + '*'}
              .selectables=${[true, false]}
              .renderString=${key => translationController.translate(key ? 'yes' : 'no')}
            ></tr-dropdown-selection>
          </x-flex>
    </br>
          </x-flex>
    </br>

          <!-- studium -->
          <x-flex space="sm">
            <tr-h4> Studium </tr-h4>
            ${stContr.genericField(['currentUni'], genericFieldOption('currentUni'))}
            <tr-dropdown-selection
              ${stContr.field('semester')}
              placeholder=${translationController.translate('icsrm', 'field', 'semester') + '*'}
              .selectables=${[
                ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(i => i.toString()),
                'bereits abgeschlossen',
              ]}
            ></tr-dropdown-selection>

            ${stContr.genericField(['courseOfStudy'], genericFieldOption('courseOfStudy'))}
            ${stContr.genericField(['department'], genericFieldOption('department'))}
            ${stContr.genericField(['desiredDegree'], genericFieldOption('desiredDegree'))}
          </x-flex>
        </x-flex>
      </x-flex>
    `;
};

/**
 * Optisch abgetrennter Abschnitt am Ende des ICS-RM-Anmeldeformulars: Pflichtfrage zur Wirkungsmessung
 * und - nur bei "ja" - die freiwillige private E-Mail-Adresse.
 *
 * Bewusst nicht Teil von `renderIcsrmFields`, weil dieser Helper beim Deutschkurs mitten im Formular
 * eingehängt wird; der Abschnitt gehört aber ans Ende beider Formulare.
 */
export const renderIcsrmImpactMeasurementSection = (
  stContr: GenericFieldFormController<IcsrmStammdaten>,
  translationController: TranslationController<BHTranslationDictionary>,
) => {
  const institutionID = stContr.document?.institutionID;
  const privacyHref = A.absoluteUrl(institutionID ? `/datenschutz?institution=${institutionID}` : '/datenschutz');

  return html`
    <x-hr></x-hr>

    <x-flex space="md">
      <tr-h4>${translationController.translate('icsrm', 'impactMeasurementTitle')}</tr-h4>

      <tr-text>
        ${translationController.translate('icsrm', 'impactMeasurementExpl')}
        <x-link href=${privacyHref} target="_blank" ${css({ display: 'inline', textDecoration: 'underline' })}>
          ${translationController.translate('icsrm', 'privacyPolicyLabel')}
        </x-link>
      </tr-text>

      <tr-dropdown-selection
        ${stContr.field('impactMeasurementConsent')}
        placeholder=${translationController.translate('icsrm', 'field', 'impactMeasurementConsent') + '*'}
        .selectables=${[true, false]}
        .renderString=${key => translationController.translate(key ? 'yes' : 'no')}
        @value-picked=${() => (stContr as any).host?.requestUpdate?.()}
      ></tr-dropdown-selection>

      ${stContr.document?.impactMeasurementConsent === true
        ? html`
            <tr-text>${translationController.translate('icsrm', 'privateEmailExpl')}</tr-text>
            ${stContr.genericField(['privateEmail'], {
              placeholder: translationController.translate('icsrm', 'field', 'privateEmail') as string,
              placeholderMode: 'static-floating',
            })}
          `
        : nothing}
    </x-flex>
  `;
};

export const icsrmRegistrationUserDataPlugin: RegistrationUserDataPlugin<IcsrmStammdaten> = {
  id: 'registration::user-data::icsrm',
  type: 'registration::user-data',
  extraUserFields: [['dateOfBirth']],
  render: (stContr, translationController) => html`
    <x-flex space="lg">
      ${renderIcsrmFields(stContr, translationController)}
      ${renderIcsrmImpactMeasurementSection(stContr, translationController)}
    </x-flex>
  `,
};
